import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePremium } from '../context/PremiumContext';
import { Button } from '../components/ui/button';
import { ChevronLeft, CheckCircle, Zap, Loader2, Crown } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { MealSwapDialog } from '../components/nutrition/MealSwapDialog';
import { MealRawTextEditorDialog } from '../components/nutrition/MealRawTextEditorDialog';

// Split sub-components — see /app/frontend/src/components/program-detail/
import { ProgramHeroBanner } from '../components/program-detail/ProgramHeroBanner';
import { ProgramStatsCard } from '../components/program-detail/ProgramStatsCard';
import { ProgressCard } from '../components/program-detail/ProgressCard';
import { PurchaseOptions } from '../components/program-detail/PurchaseOptions';
import { WeeklyScheduleCard } from '../components/program-detail/WeeklyScheduleCard';
import { PersonalizedPlanSection } from '../components/program-detail/PersonalizedPlanSection';
import { NutritionPlanSection } from '../components/program-detail/NutritionPlanSection';

const LOGO_URL = '/logo.webp';

const goalLabels = {
  weight_loss: 'Weight Loss', muscle_building: 'Muscle Building', endurance: 'Endurance',
  general_fitness: 'General Fitness', ultimate_fitness: 'Ultimate Fitness',
  strength: 'Strength', core_strength: 'Core Strength',
};

const mealSignature = (foods) => (foods || '').trim().toLowerCase().slice(0, 200);

export const ProgramDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const { isPremium } = usePremium();
  const [program, setProgram] = useState(null);
  const [myProgram, setMyProgram] = useState(null);
  const [workouts, setWorkouts] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState(1);
  const [loading, setLoading] = useState(true);
  const [purchased, setPurchased] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [myPlan, setMyPlan] = useState(null);
  const [generatingPlan, setGeneratingPlan] = useState(false);
  const [planWeek, setPlanWeek] = useState(1);
  const [nutritionPlan, setNutritionPlan] = useState(null);
  const [generatingNutrition, setGeneratingNutrition] = useState(false);
  const [nutritionWeek, setNutritionWeek] = useState(1);
  const [groceryList, setGroceryList] = useState(null);
  const [generatingGrocery, setGeneratingGrocery] = useState(false);
  const [groceryWeeks, setGroceryWeeks] = useState(1);
  const [swapDialogOpen, setSwapDialogOpen] = useState(false);
  const [swapTarget, setSwapTarget] = useState(null);
  const [rawTextTarget, setRawTextTarget] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [favoritingMeal, setFavoritingMeal] = useState(null);
  const [removingFavorite, setRemovingFavorite] = useState(null);

  // Admin has full access; Premium has access only to premium-accessible
  // programs; otherwise the user must purchase. Nutrition plans are always
  // premium-only (admins exempt).
  const isAdmin = user?.is_admin === true;
  const isPremiumAccessible = program?.premium_accessible === true;
  const hasAccess = purchased || isAdmin || (isPremium && isPremiumAccessible);
  const nutritionPremiumLocked = !isPremium && !isAdmin;

  useEffect(() => { fetchData(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [programRes, myProgramRes, workoutsRes, purchaseCheckRes] = await Promise.all([
        api().get(`/programs/${id}`),
        api().get('/programs/my/current'),
        api().get('/workouts'),
        api().get(`/payments/check/${id}`),
      ]);
      setProgram(programRes.data);
      setMyProgram(myProgramRes.data);
      setWorkouts(workoutsRes.data);
      setPurchased(purchaseCheckRes.data?.purchased || false);

      if (myProgramRes.data?.user_program?.program_id === id) {
        setSelectedWeek(myProgramRes.data.user_program.current_week);
      }

      try {
        const planRes = await api().get(`/plans/my/${id}`);
        if (planRes.data?.plan) setMyPlan(planRes.data.plan);
      } catch (e) { console.debug('No workout plan:', e.message); }

      try {
        const nutRes = await api().get(`/nutrition-plans/my/${id}`);
        if (nutRes.data?.plan) {
          setNutritionPlan(nutRes.data.plan);
          try {
            const grocRes = await api().get(`/nutrition-plans/grocery-list/${nutRes.data.plan.id}`);
            if (grocRes.data?.grocery_list) setGroceryList(grocRes.data.grocery_list);
          } catch (e) { console.debug('No grocery list:', e.message); }
        }
      } catch (e) { console.debug('No nutrition plan:', e.message); }

      try {
        const favRes = await api().get('/nutrition-plans/favorites/list');
        setFavorites(favRes.data?.favorites || []);
      } catch (e) { console.debug('No favorites:', e.message); }
    } catch (err) {
      if (err.response?.status === 402) {
        toast.info(err.response?.data?.detail || 'This program is for premium members. Upgrade to unlock.');
        navigate('/subscription');
      } else {
        toast.error('Failed to load program');
        navigate('/programs');
      }
    }
    setLoading(false);
  };

  // ===== Workout plan handlers =====
  const generatePlan = async () => {
    setGeneratingPlan(true);
    try {
      const res = await api().post(`/plans/generate/${id}`);
      setMyPlan(res.data.plan);
      toast.success(res.data.message || 'Your personalized plan is ready!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate plan');
    }
    setGeneratingPlan(false);
  };

  const regeneratePlan = async () => {
    if (!window.confirm('This will replace your current plan. Continue?')) return;
    setGeneratingPlan(true);
    try {
      const res = await api().post(`/plans/regenerate/${id}`);
      setMyPlan(res.data.plan);
      toast.success('New plan generated!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to regenerate plan');
    }
    setGeneratingPlan(false);
  };

  const toggleDayComplete = async (dayIndex) => {
    if (!myPlan?.id) return;
    try {
      const res = await api().post(`/plans/my/${myPlan.id}/complete-day`, {
        week: planWeek,
        day_index: dayIndex,
      });
      setMyPlan(prev => {
        const updated = { ...prev, weeks: [...prev.weeks] };
        const weekData = { ...updated.weeks[planWeek - 1] };
        weekData.days = [...weekData.days];
        weekData.days[dayIndex] = { ...weekData.days[dayIndex], completed: res.data.completed };
        updated.weeks[planWeek - 1] = weekData;
        updated.progress = res.data.progress;
        updated.completed_days = res.data.completed_days;
        updated.total_workout_days = res.data.total_workout_days;
        return updated;
      });
    } catch {
      toast.error('Failed to update');
    }
  };

  // ===== Nutrition plan handlers =====
  const generateNutritionPlan = async () => {
    setGeneratingNutrition(true);
    try {
      const res = await api().post(`/nutrition-plans/generate/${id}`);
      setNutritionPlan(res.data.plan);
      toast.success(res.data.message || 'Nutrition plan ready!');
    } catch (err) {
      if (err.response?.status === 402) {
        toast.error('Nutrition plans are exclusive to premium members.');
        navigate('/subscription');
      } else {
        toast.error(err.response?.data?.detail || 'Failed to generate nutrition plan');
      }
    }
    setGeneratingNutrition(false);
  };

  const regenerateNutritionPlan = async () => {
    if (!window.confirm('This will replace your current nutrition plan. Continue?')) return;
    setGeneratingNutrition(true);
    try {
      const res = await api().post(`/nutrition-plans/regenerate/${id}`);
      setNutritionPlan(res.data.plan);
      setGroceryList(null);
      toast.success('New nutrition plan generated!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to regenerate');
    }
    setGeneratingNutrition(false);
  };

  const generateGroceryList = async () => {
    if (!nutritionPlan?.id) return;
    setGeneratingGrocery(true);
    try {
      const res = await api().post(`/nutrition-plans/grocery-list/${nutritionPlan.id}`, { weeks: groceryWeeks });
      setGroceryList(res.data.grocery_list);
      toast.success(res.data.message || 'Grocery list ready!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate grocery list');
    }
    setGeneratingGrocery(false);
  };

  // ===== Favorites handlers =====
  const toggleFavorite = async (meal) => {
    if (!meal?.foods) return;
    const existing = favorites.find((f) => f.signature === mealSignature(meal.foods));
    if (existing) {
      setRemovingFavorite(existing.id);
      try {
        await api().delete(`/nutrition-plans/favorites/${existing.id}`);
        setFavorites((prev) => prev.filter((f) => f.id !== existing.id));
        toast.success('Removed from favorites');
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to remove');
      }
      setRemovingFavorite(null);
      return;
    }
    setFavoritingMeal(mealSignature(meal.foods));
    try {
      const res = await api().post('/nutrition-plans/favorites', { meal });
      if (res.data?.favorite) {
        setFavorites((prev) => (prev.some((f) => f.id === res.data.favorite.id) ? prev : [res.data.favorite, ...prev]));
      }
      toast.success(res.data.message || 'Added to favorites!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save favorite');
    }
    setFavoritingMeal(null);
  };

  const removeFavorite = async (favId) => {
    setRemovingFavorite(favId);
    try {
      await api().delete(`/nutrition-plans/favorites/${favId}`);
      setFavorites((prev) => prev.filter((f) => f.id !== favId));
      toast.success('Removed from favorites');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove');
    }
    setRemovingFavorite(null);
  };

  // ===== Checkout handlers =====
  const handleStripeCheckout = async () => {
    setCheckingOut(true);
    try {
      const response = await api().post(`/payments/stripe/create-checkout?program_id=${id}`);
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start checkout');
    }
    setCheckingOut(false);
  };

  const handlePayPalCheckout = async () => {
    setCheckingOut(true);
    try {
      const response = await api().post(`/payments/paypal/create-order?program_id=${id}`);
      if (response.data.approval_url) {
        window.location.href = response.data.approval_url;
      } else {
        toast.error('Failed to create PayPal order');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start PayPal checkout');
    }
    setCheckingOut(false);
  };

  const enrollInProgram = async () => {
    setEnrolling(true);
    try {
      await api().post(`/programs/enroll/${id}`);
      toast.success("Enrolled! Let's start your journey!");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to enroll');
    }
    setEnrolling(false);
  };

  const isEnrolled = myProgram?.user_program?.program_id === id;
  const currentWeek = myProgram?.user_program?.current_week || 1;
  const weekData = program?.weeks?.find(w => w.week === selectedWeek);
  const price = program?.price || 49.99;

  if (loading || !program) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin h-12 w-12 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="program-detail-page">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/programs" className="flex items-center gap-2 text-white hover:text-primary transition-colors">
            <ChevronLeft className="h-5 w-5" />
            <span className="font-medium">Back to Programs</span>
          </Link>
          <img src={LOGO_URL} alt="Ascend & Surpass" className="h-10 w-10" />
          <div className="w-24" />
        </div>
      </header>

      <main className="container mx-auto px-6 py-8">
        <ProgramHeroBanner
          program={program}
          hasAccess={hasAccess}
          isAdmin={isAdmin}
          isPremium={isPremium}
          purchased={purchased}
        />

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-card border border-border p-6">
              <h3 className="font-heading text-xl text-white uppercase tracking-wider mb-4">About This Program</h3>
              <p className="text-muted-foreground leading-relaxed">{program.description}</p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
              <WeeklyScheduleCard
                program={program}
                workouts={workouts}
                selectedWeek={selectedWeek}
                weekData={weekData}
                isEnrolled={isEnrolled}
                currentWeek={currentWeek}
                hasAccess={hasAccess}
                onSelectWeek={setSelectedWeek}
              />
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {isEnrolled && (
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
                <ProgressCard currentWeek={currentWeek} totalWeeks={program.duration_weeks} />
              </motion.div>
            )}

            {!isEnrolled && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-card border border-border p-6"
              >
                {hasAccess ? (
                  <>
                    {/* Access banner */}
                    <div className="flex items-center gap-2 text-green-400 mb-4">
                      {isPremium && !purchased && !isAdmin ? (
                        <>
                          <Crown className="h-5 w-5 text-yellow-400" />
                          <span className="font-heading uppercase tracking-wider text-yellow-400">Premium Access</span>
                        </>
                      ) : (
                        <>
                          <CheckCircle className="h-5 w-5" />
                          <span className="font-heading uppercase tracking-wider">
                            {isAdmin ? 'Admin Access' : 'Program Owned'}
                          </span>
                        </>
                      )}
                    </div>

                    <h3 className="font-heading text-xl text-white uppercase tracking-wider mb-4">Ready to Start?</h3>
                    <p className="text-muted-foreground mb-6">
                      Commit to {program.duration_weeks} weeks of structured training to reach your{' '}
                      {(goalLabels[program.goal] || program.goal || 'fitness').toLowerCase()} goals.
                    </p>

                    <Button
                      onClick={enrollInProgram}
                      className="w-full h-14 bg-primary text-primary-foreground font-heading text-lg tracking-wider uppercase hover:bg-primary/90"
                      disabled={myProgram?.user_program || enrolling}
                      data-testid="enroll-button"
                    >
                      {enrolling ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <Zap className="mr-2 h-5 w-5" />}
                      {myProgram?.user_program ? 'Finish Current Program' : 'Start Program'}
                    </Button>

                    {myProgram?.user_program && (
                      <p className="text-muted-foreground text-sm text-center mt-3">
                        You're currently enrolled in another program
                      </p>
                    )}

                    {isAdmin && (
                      <Link to={`/admin/program/${id}`} className="block mt-4">
                        <Button variant="outline" className="w-full border-primary text-primary hover:bg-primary/10">
                          Edit Program
                        </Button>
                      </Link>
                    )}

                    <PersonalizedPlanSection
                      myPlan={myPlan}
                      generatingPlan={generatingPlan}
                      planWeek={planWeek}
                      setPlanWeek={setPlanWeek}
                      onGenerate={generatePlan}
                      onRegenerate={regeneratePlan}
                      onToggleDayComplete={toggleDayComplete}
                    />

                    <NutritionPlanSection
                      user={user}
                      nutritionPlan={nutritionPlan}
                      generatingNutrition={generatingNutrition}
                      premiumLocked={nutritionPremiumLocked}
                      nutritionWeek={nutritionWeek}
                      setNutritionWeek={setNutritionWeek}
                      favorites={favorites}
                      favoritingMeal={favoritingMeal}
                      removingFavorite={removingFavorite}
                      groceryList={groceryList}
                      groceryWeeks={groceryWeeks}
                      setGroceryWeeks={setGroceryWeeks}
                      generatingGrocery={generatingGrocery}
                      onGenerate={generateNutritionPlan}
                      onRegenerate={regenerateNutritionPlan}
                      onUnlockPremium={() => navigate('/subscription')}
                      onToggleFavorite={toggleFavorite}
                      onRemoveFavorite={removeFavorite}
                      onOpenSwap={({ meal, mealIndex }) => {
                        setSwapTarget({ weekIndex: nutritionWeek - 1, mealIndex, meal });
                        setSwapDialogOpen(true);
                      }}
                      onOpenRawText={({ weekIndex, mealIndex, initialText, mealLabel }) => {
                        setRawTextTarget({ weekIndex, mealIndex, initialText, mealLabel });
                      }}
                      onGenerateGrocery={generateGroceryList}
                      onClearGrocery={() => setGroceryList(null)}
                    />
                  </>
                ) : (
                  <PurchaseOptions
                    program={program}
                    price={price}
                    checkingOut={checkingOut}
                    onStripe={handleStripeCheckout}
                    onPayPal={handlePayPalCheckout}
                  />
                )}
              </motion.div>
            )}

            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }}>
              <ProgramStatsCard program={program} />
            </motion.div>
          </div>
        </div>
      </main>

      <MealSwapDialog
        isOpen={swapDialogOpen}
        onClose={() => { setSwapDialogOpen(false); setSwapTarget(null); }}
        api={api}
        planId={nutritionPlan?.id}
        weekIndex={swapTarget?.weekIndex}
        mealIndex={swapTarget?.mealIndex}
        originalMeal={swapTarget?.meal || {}}
        onSwapApplied={(updatedPlan) => setNutritionPlan(updatedPlan)}
        favorites={favorites}
        onFavoriteAdded={(fav) => setFavorites((prev) => (prev.some((f) => f.id === fav.id) ? prev : [fav, ...prev]))}
      />

      <MealRawTextEditorDialog
        open={!!rawTextTarget}
        onClose={() => setRawTextTarget(null)}
        api={api}
        planId={nutritionPlan?.id}
        weekIndex={rawTextTarget?.weekIndex}
        mealIndex={rawTextTarget?.mealIndex}
        initialText={rawTextTarget?.initialText || ''}
        mealLabel={rawTextTarget?.mealLabel}
        onSaved={(updatedPlan) => updatedPlan && setNutritionPlan(updatedPlan)}
      />
    </div>
  );
};
